import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SearchIcon, FilterIcon, DownloadIcon, PlusIcon, MoreHorizontalIcon, StoreIcon, SmartphoneIcon, GlobeIcon, CreditCardIcon } from 'lucide-react';
import { formatMoney } from '../../../shared/lib/money';
import { outletsData, Outlet } from '../mocks/outletsData';
export function OutletDirectory() {
  const {
    t
  } = useTranslation('common');
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedOutlets, setSelectedOutlets] = useState<Set<string>>(new Set());
  const filteredOutlets = outletsData.filter(outlet => outlet.name.toLowerCase().includes(searchTerm.toLowerCase()) || outlet.subMerchantId.toLowerCase().includes(searchTerm.toLowerCase()) || outlet.mall.toLowerCase().includes(searchTerm.toLowerCase()));
  const toggleSelectAll = () => {
    if (selectedOutlets.size === filteredOutlets.length) {
      setSelectedOutlets(new Set());
    } else {
      setSelectedOutlets(new Set(filteredOutlets.map(o => o.id)));
    }
  };
  const toggleSelect = (id: string) => {
    const newSelected = new Set(selectedOutlets);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedOutlets(newSelected);
  };
  const getStatusColor = (status: Outlet['status']) => {
    switch (status) {
      case 'Active':
        return 'bg-green-100 text-green-800';
      case 'Onboarding':
        return 'bg-blue-100 text-blue-800';
      case 'Suspended':
        return 'bg-red-100 text-red-800';
      case 'Closed':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  const renderChannelIcon = (channel: string) => {
    switch (channel) {
      case 'POS':
        return <CreditCardIcon size={14} className="text-gray-500" data-id="element-7659" />;
      case 'E-com':
        return <GlobeIcon size={14} className="text-gray-500" data-id="element-7660" />;
      case 'APM':
        return <SmartphoneIcon size={14} className="text-gray-500" data-id="element-7661" />;
      default:
        return null;
    }
  };
  return <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-7662">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8" data-id="element-7663">
        <div data-id="element-7664">
          <h1 className="text-2xl font-bold text-gray-900" data-id="element-7665">Outlet Directory</h1>
          <p className="text-sm text-gray-500 mt-1" data-id="element-7666">
            Manage all sub-merchants across your properties
          </p>
        </div>
        <div className="flex items-center gap-3" data-id="element-7667">
          <button className="px-4 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors shadow-sm flex items-center gap-2" data-id="element-7668">
            <DownloadIcon size={16} data-id="element-7669" />
            {t('actions.export')}
          </button>
          <button onClick={() => navigate('/outlets/onboard')} className="px-4 py-2 bg-[var(--brand-primary)] text-[var(--brand-primarytextonwhite)] text-sm font-medium rounded-lg hover:bg-[var(--brand-primaryhover)] transition-colors shadow-sm flex items-center gap-2" data-id="element-7670">
            <PlusIcon size={16} data-id="element-7671" />
            Add Outlet
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden" data-id="element-7672">
        {/* Toolbar */}
        <div className="p-4 border-b border-gray-200 flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-7673">
          <div className="relative max-w-md w-full" data-id="element-7674">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-7675">
              <SearchIcon size={16} className="text-gray-400" data-id="element-7676" />
            </div>
            <input type="text" placeholder="Search outlets, IDs, or malls..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 sm:text-sm" data-id="element-7677" />
          </div>
          <div className="flex items-center gap-3" data-id="element-7678">
            {selectedOutlets.size > 0 && <span className="text-sm text-gray-500 font-medium" data-id="element-7679">
                {selectedOutlets.size} selected
              </span>}
            <button className="px-3 py-2 bg-white border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors shadow-sm flex items-center gap-2" data-id="element-7680">
              <FilterIcon size={16} data-id="element-7681" />
              {t('actions.filter')}
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto" data-id="element-7682">
          <table className="min-w-full divide-y divide-gray-200" data-id="element-7683">
            <thead className="bg-gray-50" data-id="element-7684">
              <tr data-id="element-7685">
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-10" data-id="element-7686">
                  <input type="checkbox" className="rounded border-gray-300 text-[var(--brand-primary)] focus:ring-[var(--brand-primary)]" checked={selectedOutlets.size === filteredOutlets.length && filteredOutlets.length > 0} onChange={toggleSelectAll} data-id="element-7687" />
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-7688">
                  Outlet
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-7689">
                  Location
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-7690">
                  Channels
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-7691">
                  Period GMV
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-7692">
                  Status
                </th>
                <th scope="col" className="relative px-6 py-3" data-id="element-7693">
                  <span className="sr-only" data-id="element-7694">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-7695">
              {filteredOutlets.map(outlet => <tr key={outlet.id} className="hover:bg-gray-50 cursor-pointer transition-colors" onClick={e => {
              // Prevent navigation if clicking checkbox or actions
              if ((e.target as HTMLElement).closest('input[type="checkbox"]') || (e.target as HTMLElement).closest('button')) return;
              navigate(`/outlets/outlet/${outlet.id}`);
            }} data-id="element-7696">
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-7697">
                    <input type="checkbox" className="rounded border-gray-300 text-[var(--brand-primary)] focus:ring-[var(--brand-primary)]" checked={selectedOutlets.has(outlet.id)} onChange={() => toggleSelect(outlet.id)} data-id="element-7698" />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-7699">
                    <div className="flex items-center" data-id="element-7700">
                      <div className="flex-shrink-0 h-10 w-10 bg-gray-100 rounded-lg flex items-center justify-center" data-id="element-7701">
                        <StoreIcon size={20} className="text-gray-500" data-id="element-7702" />
                      </div>
                      <div className="ml-4" data-id="element-7703">
                        <div className="text-sm font-medium text-gray-900" data-id="element-7704">
                          {outlet.name}
                        </div>
                        <div className="text-sm text-gray-500" data-id="element-7705">
                          {outlet.subMerchantId} • {outlet.category}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-7706">
                    <div className="text-sm text-gray-900" data-id="element-7707">{outlet.mall}</div>
                    <div className="text-sm text-gray-500" data-id="element-7708">
                      {outlet.floorZone}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-7709">
                    <div className="flex gap-1.5" data-id="element-7710">
                      {outlet.channels.map(channel => <div key={channel} className="px-2 py-1 bg-gray-100 rounded text-xs font-medium text-gray-600 flex items-center gap-1" title={channel} data-id="element-7711">
                          {renderChannelIcon(channel)}
                          <span className="hidden sm:inline" data-id="element-7712">{channel}</span>
                        </div>)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right" data-id="element-7713">
                    <div className="text-sm font-medium text-gray-900" data-id="element-7714">
                      {formatMoney(outlet.periodGmv)}
                    </div>
                    <div className="text-sm text-gray-500" data-id="element-7715">
                      {outlet.txnCount.toLocaleString()} txns
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap" data-id="element-7716">
                    <span className={`px-2.5 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(outlet.status)}`} data-id="element-7717">
                      {outlet.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-7718">
                    <button className="text-gray-400 hover:text-gray-600 p-1 rounded-md hover:bg-gray-100 transition-colors" data-id="element-7719">
                      <MoreHorizontalIcon size={18} data-id="element-7720" />
                    </button>
                  </td>
                </tr>)}
              {filteredOutlets.length === 0 && <tr data-id="element-7721">
                  <td colSpan={7} className="px-6 py-12 text-center text-gray-500" data-id="element-7722">
                    No outlets found matching your search.
                  </td>
                </tr>}
            </tbody>
          </table>
        </div>

        {/* Pagination mock */}
        <div className="bg-white px-4 py-3 border-t border-gray-200 flex items-center justify-between sm:px-6" data-id="element-7723">
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between" data-id="element-7724">
            <div data-id="element-7725">
              <p className="text-sm text-gray-700" data-id="element-7726">
                Showing <span className="font-medium" data-id="element-7727">1</span> to{' '}
                <span className="font-medium" data-id="element-7728">{filteredOutlets.length}</span> of{' '}
                <span className="font-medium" data-id="element-7729">{filteredOutlets.length}</span>{' '}
                results
              </p>
            </div>
            <div data-id="element-7730">
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination" data-id="element-7731">
                <button className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50" data-id="element-7732">
                  Previous
                </button>
                <button className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-blue-50 text-blue-600 text-sm font-medium" data-id="element-7733">
                  1
                </button>
                <button className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50" data-id="element-7734">
                  Next
                </button>
              </nav>
            </div>
          </div>
        </div>
      </div>
    </div>;
}